import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { FindingsPanel } from './FindingsPanel';
import { ReviewDecisionForm, type ReviewPayload } from './ReviewDecisionForm';
import { ReviewSummary } from './ReviewSummary';
import { mapReportToFindings } from './reportUtils';
import type { GatePrerequisiteState } from './gatePrerequisites';
import type { AnnotationAction, FinalDecision, GateDecision, GateReport, Job, JobReview } from '@/types';
import type { AnnotationMap } from './AnnotationForm';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

interface HumanReviewTabProps {
  job: Job;
  report: GateReport;
  className?: string;
}

type ReviewState = 'loading' | 'reviewing' | 'submitted' | 'error';

/**
 * HumanReviewTab — orchestrator for the human review state machine.
 * FR-077: every gate outcome (including APPROVED) requires explicit human approval.
 * States: loading → reviewing (annotations + decision) → submitted (read-only)
 * Visible only to reviewer/admin roles.
 */
export function HumanReviewTab({ job, report, className }: HumanReviewTabProps) {
  const { canReview, canApproveSeniorLocked, profile } = useAuth();
  const navigate = useNavigate();

  const [reviewState, setReviewState] = useState<ReviewState>('loading');
  const [existingReview, setExistingReview] = useState<JobReview | null>(null);
  const [annotations, setAnnotations] = useState<AnnotationMap>(new Map());
  const [loadError, setLoadError] = useState<string | null>(null);
  const [gatePrerequisites, setGatePrerequisites] = useState<GatePrerequisiteState | null>(null);

  const findings = mapReportToFindings(report);
  const blockingFindings = findings.filter((f) => f.severity === 'BLOCKING');
  const annotatedBlockingCount = blockingFindings.filter((f) => annotations.has(f.id)).length;

  // Fetch existing review on mount
  useEffect(() => {
    if (!canReview) { setReviewState('reviewing'); return; }
    api.get<JobReview | null>(`/api/v1/jobs/${job.id}/review`)
      .then((review) => {
        if (review) { setExistingReview(review); setReviewState('submitted'); }
      else setReviewState('reviewing');
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) setReviewState('reviewing');
        else { setLoadError('Failed to load review status.'); setReviewState('error'); }
      });
  }, [job.id, canReview]);

  // Which earlier gates still block an approving decision. Failing to load this
  // leaves it null, which blocks nothing — the server enforces the sequence
  // regardless, so a lost round-trip is better than a form nobody can submit.
  useEffect(() => {
    if (!canReview) return;
    api.get<GatePrerequisiteState>(`/api/v1/jobs/${job.id}/review/prerequisites`)
      .then(setGatePrerequisites)
      .catch(() => { setGatePrerequisites(null); });
  }, [job.id, canReview]);

  function handleAnnotate(findingId: string, action: AnnotationAction, comment: string) {
    setAnnotations((prev) => {
      const next = new Map(prev);
      next.set(findingId, { action, comment });
      return next;
    });
  }

  async function handleSubmit(payload: ReviewPayload) {
    try {
      const review = await api.post<JobReview>(`/api/v1/jobs/${job.id}/review`, payload);
      setExistingReview(review);
      setReviewState('submitted');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Two different 409s land here: a decision was already recorded, or an
        // earlier gate is unapproved. Only the first is fixed by reloading, so
        // re-read the prerequisites to tell them apart rather than reloading the
        // page out from under a reviewer who just needs to go approve Gate 2.
        const fresh = await api
          .get<GatePrerequisiteState>(`/api/v1/jobs/${job.id}/review/prerequisites`)
          .catch(() => null);
        setGatePrerequisites(fresh);
        if (!fresh || fresh.satisfied) {
          setLoadError('A decision was already recorded. Refreshing...');
          setTimeout(() => { window.location.reload(); }, 2000);
        }
      }
      throw err; // re-throw so ReviewDecisionForm can show inline error
    }
  }

  if (!canReview) {
    return (
      <Alert className={className}>
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        <AlertDescription>Only reviewers and administrators can access the review panel.</AlertDescription>
      </Alert>
    );
  }

  if (reviewState === 'loading') {
    return (
      <div className={cn('space-y-4', className)} role="status" aria-label="Loading review">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    );
  }

  if (reviewState === 'error') {
    return (
      <Alert variant="destructive" className={className}>
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        <AlertDescription>{loadError ?? 'An unexpected error occurred.'}</AlertDescription>
      </Alert>
    );
  }

  if (reviewState === 'submitted' && existingReview) {
    // The stored JobReview keeps review_status + final_decision but not the
    // original decision_type, so reconstruct it: escalated → escalate; a final
    // decision that differs from the AI recommendation → override; otherwise
    // accept. Without this, a recorded override rendered as "accept".
    const gateToFinal = (gd: GateDecision): FinalDecision =>
      gd === 'MANUAL_REVIEW_REQUIRED' ? 'ESCALATED' : gd;
    const summaryDecisionType: 'accept' | 'override' | 'escalate' =
      existingReview.review_status === 'escalated'
        ? 'escalate'
        : existingReview.final_decision != null &&
            existingReview.final_decision !== gateToFinal(report.decision)
          ? 'override'
          : 'accept';
    return (
      <div className={cn('space-y-4', className)}>
        <ReviewSummary
          reviewerName={profile?.full_name ?? profile?.email ?? 'Reviewer'}
          reviewedAt={existingReview.reviewed_at ?? existingReview.created_at}
          decisionType={summaryDecisionType}
          aiDecision={report.decision}
          finalDecision={existingReview.final_decision}
          justification={existingReview.justification ?? ''}
          // Per-finding annotation counts aren't persisted on JobReview; the
          // summary section stays hidden (it only renders when total > 0).
          annotationsSummary={{ agree: 0, disagree: 0, discuss: 0 }}
        />
        <Button variant="outline" onClick={() => { navigate('/dashboard?tab=review'); }} className="gap-2">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to Review Queue
        </Button>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Annotation completeness — live region for screen readers */}
      <div className="flex flex-wrap gap-4 text-sm" aria-live="polite" aria-atomic="true">
        <span className={cn(
          annotatedBlockingCount >= blockingFindings.length && blockingFindings.length > 0
            ? 'text-green-700 dark:text-green-400'
            : 'text-muted-foreground',
        )}>
          Blocking issues: {annotatedBlockingCount} of {blockingFindings.length} annotated
        </span>
      </div>

      <FindingsPanel findings={findings} annotations={annotations} onAnnotate={handleAnnotate} />

      <section aria-labelledby="decision-form-heading">
        <h3 id="decision-form-heading" className="text-base font-semibold mb-4">
          Final Review Decision
        </h3>
        <ReviewDecisionForm
          aiDecision={report.decision}
          reviewerName={profile?.full_name ?? profile?.email ?? 'Reviewer'}
          blockingCount={blockingFindings.length}
          annotatedBlockingCount={annotatedBlockingCount}
          annotations={annotations}
          onSubmit={handleSubmit}
          requiresSeniorApproval={job.requires_senior_approval ?? false}
          seniorApprovalReasons={report.senior_approval_reasons ?? []}
          canApproveSeniorLocked={canApproveSeniorLocked}
          gatePrerequisites={gatePrerequisites}
        />
      </section>
    </div>
  );
}
